import { BOOKING_API_URL, supabase } from '../lib/supabase';

export interface WalkinSlotInput {
  room_id: string;
  start_time_id: string;
  slot_start: string;       // ISO timestamptz
  party_size: number;
  slot_index: number;
}

// One operator-typed experience-pick (an experience + price + its slots).
export interface WalkinPickInput {
  experience_id: string;
  price_cents: number;      // operator-typed per-pick price (subtotal contributor)
  slots: WalkinSlotInput[];
}

// One departure = a set of picks that share a single departure_start time.
// Multi-pick = multiple experiences in one QR / one email.
export interface WalkinDepartureInput {
  experiences: WalkinPickInput[];
}

export interface WalkinCustomerInput {
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  locale?: string;
  marketing_opt_in?: boolean;
}

export type WalkinPaymentMethod = 'cash' | 'card_terminal' | 'comp' | 'unpaid';

export interface WalkinRequest {
  // New shape — list of departures, each with one or more experience-picks.
  departures: WalkinDepartureInput[];

  // null = balie walk-in zonder e-mail: geen klantrecord, geen ticketmail; de
  // naam staat op de boeking zelf (guest_name, dan verplicht).
  customer: WalkinCustomerInput | null;
  guest_name?: string | null;
  // "Direct inchecken": na aanmaken elk vertrek direct inchecken (zelfde pad
  // als een QR-scan). Bij POS wordt dit genegeerd: dan checkt de betaling in
  // (checkin_departure_id op booking-terminal start).
  checkin?: boolean;
  payment_method: WalkinPaymentMethod;
  force?: boolean;
  locale?: string;
  notes?: string | null;
  discount_code?: string | null;
  // Alleen relevant bij payment_method='unpaid': false onderdrukt de
  // betaallink (bedrag + betaal-online knop) in de bevestigingsmail.
  // Default (weggelaten/true) = betaallink meesturen.
  send_payment_link?: boolean;
  // false = bevestigingsmail NIET bij aanmaken versturen. Gebruikt door
  // "Pinterminal (POS)": de boeking wordt als unpaid aangemaakt en direct op
  // de WisePOS afgerekend; de webhook mailt het ticket zodra de betaling
  // binnen is (een geweigerde kaart laat dus geen ticket achter in de inbox).
  send_email?: boolean;
}

// Result row per departure that came back from the edge function.
export interface WalkinDepartureResult {
  departure_id: string;
  departure_number: string;
  departure_start: string | null;
  party_size: number;
  email_sent: boolean;
  email_error: string | null;   // 'no_email' = balie walk-in zonder e-mail
  // null = niet gevraagd; true/false = resultaat van "Direct inchecken".
  checked_in?: boolean | null;
  checkin_error?: string | null;
}

export interface WalkinResponse {
  booking_id: string;
  booking_number: string;     // first departure's number (legacy alias)
  party_size: number;
  subtotal_cents: number;
  total_cents: number;
  forced: boolean;
  discount: { code: string; amount_off_cents: number } | null;
  departures: WalkinDepartureResult[];
  guest_name?: string | null;
  anonymous?: boolean;

  // Legacy convenience fields surfacing the first departure's email status.
  email_sent: boolean;
  email_error: string | null;
}

export interface WalkinError {
  error: string;
  message?: string;
  reason?: string;
}

export interface WalkinResult {
  ok: boolean;
  data: WalkinResponse | null;
  error: WalkinError | null;
  status: number;
}

export async function createWalkinBooking(
  body: WalkinRequest,
): Promise<WalkinResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    return {
      ok: false,
      data: null,
      error: { error: 'unauthenticated', message: 'No active session' },
      status: 401,
    };
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/booking-walkin-create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: {
        error: 'network_error',
        message: e instanceof Error ? e.message : String(e),
      },
      status: 0,
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-JSON */
  }

  if (!res.ok) {
    return {
      ok: false,
      data: null,
      error: (json as WalkinError) ?? {
        error: 'unknown_error',
        message: `HTTP ${res.status}`,
      },
      status: res.status,
    };
  }

  return {
    ok: true,
    data: json as WalkinResponse,
    error: null,
    status: res.status,
  };
}
